/**
 * Sound effects — all synthesized, no audio files.
 *
 * Each SFX is a short procedural sound built from oscillators,
 * noise, and filters. Designed to be crisp and satisfying.
 */

import type { AudioEngine } from './audio-engine';

export class SFX {
  private engine: AudioEngine;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  /** Jump — short sine sweep up + noise burst */
  jump(): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, t);
    osc.frequency.exponentialRampToValueAtTime(600, t + 0.08);
    gain.gain.setValueAtTime(0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc.connect(gain);
    gain.connect(output);
    osc.start(t);
    osc.stop(t + 0.1);

    // Tiny noise pop
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.03, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.3;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const nGain = ctx.createGain();
    nGain.gain.setValueAtTime(0.15, t);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 4000;
    noise.connect(hp);
    hp.connect(nGain);
    nGain.connect(output);
    noise.start(t);
    noise.stop(t + 0.03);
  }

  /** Death — noise burst + descending pitch + distortion */
  death(): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    // Descending tone
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(400, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.4);
    gain.gain.setValueAtTime(0.4, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);

    // Distortion via waveshaper
    const distortion = ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
      const x = (i / 128) - 1;
      curve[i] = (Math.PI + 4) * x / (Math.PI + 4 * Math.abs(x));
    }
    distortion.curve = curve;

    osc.connect(distortion);
    distortion.connect(gain);
    gain.connect(output);
    osc.start(t);
    osc.stop(t + 0.5);

    // Noise crash
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.3, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const nGain = ctx.createGain();
    nGain.gain.setValueAtTime(0.25, t);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2000;
    bp.Q.value = 0.5;
    noise.connect(bp);
    bp.connect(nGain);
    nGain.connect(output);
    noise.start(t);
    noise.stop(t + 0.3);
  }

  /** Star collect — bright bell tone */
  starCollect(): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    const freqs = [880, 1320, 1760]; // harmonics
    for (let i = 0; i < freqs.length; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freqs[i];
      const vol = 0.2 / (i + 1);
      gain.gain.setValueAtTime(vol, t + i * 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4 + i * 0.04);
      osc.connect(gain);
      gain.connect(output);
      osc.start(t + i * 0.04);
      osc.stop(t + 0.5);
    }
  }

  /** Graze — brief high-freq tick */
  graze(): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 2500;
    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
    osc.connect(gain);
    gain.connect(output);
    osc.start(t);
    osc.stop(t + 0.04);
  }

  /** Menu select — click/blip */
  menuSelect(): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.exponentialRampToValueAtTime(1200, t + 0.03);
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 3000;

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(output);
    osc.start(t);
    osc.stop(t + 0.06);
  }

  /** Checkpoint — ascending 3-note arpeggio */
  checkpoint(): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    const notes = [440, 554.37, 659.25]; // A4, C#5, E5
    for (let i = 0; i < notes.length; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = notes[i];
      const start = t + i * 0.08;
      gain.gain.setValueAtTime(0.2, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.2);
      osc.connect(gain);
      gain.connect(output);
      osc.start(start);
      osc.stop(start + 0.2);
    }
  }

  /** Boing — pitched spring sweep for jump pads. Pitch scales with effective tier. */
  boing(tier: 'small' | 'medium' | 'large' | 'extra-large'): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    // Lower pitch = more powerful pad
    const pitchScale = (
      tier === 'small'      ? 1.3 :
      tier === 'medium'     ? 1.0 :
      tier === 'large'      ? 0.75 :
      0.6   // extra-large
    );

    // Primary triangle osc — sproingy contour
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(180 * pitchScale, t);
    osc.frequency.exponentialRampToValueAtTime(380 * pitchScale, t + 0.06);
    osc.frequency.exponentialRampToValueAtTime(220 * pitchScale, t + 0.18);
    oscGain.gain.setValueAtTime(0.3, t);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);

    // Lowpass with light resonance for spring-iness
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1200;
    lp.Q.value = 4;

    osc.connect(lp);
    lp.connect(oscGain);
    oscGain.connect(output);
    osc.start(t);
    osc.stop(t + 0.22);

    // Detuned harmonic (one octave up) — adds shimmer
    const harm = ctx.createOscillator();
    const harmGain = ctx.createGain();
    harm.type = 'sine';
    harm.frequency.setValueAtTime(360 * pitchScale, t);
    harm.frequency.exponentialRampToValueAtTime(760 * pitchScale, t + 0.06);
    harm.frequency.exponentialRampToValueAtTime(440 * pitchScale, t + 0.18);
    harmGain.gain.setValueAtTime(0.08, t);
    harmGain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    harm.connect(harmGain);
    harmGain.connect(output);
    harm.start(t);
    harm.stop(t + 0.18);

    // Noise click at attack
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.02, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.4;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const nGain = ctx.createGain();
    nGain.gain.setValueAtTime(0.1, t);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 0.02);
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 3000;
    noise.connect(hp);
    hp.connect(nGain);
    nGain.connect(output);
    noise.start(t);
    noise.stop(t + 0.02);
  }

  /** BoingOrb — deeper, smoother variant of boing for mid-air orbs. */
  boingOrb(tier: 'small' | 'medium' | 'large'): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    // Lower pitch = more powerful orb (same scaling shape as pads)
    const pitchScale = tier === 'small' ? 1.3 : tier === 'medium' ? 1.0 : 0.75;

    // Sine osc — smoother than the triangle used for pads, gives a more "orb" tone
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = 'sine';
    // Roughly 60% of boing's frequency range → noticeably deeper
    osc.frequency.setValueAtTime(110 * pitchScale, t);
    osc.frequency.exponentialRampToValueAtTime(230 * pitchScale, t + 0.07);
    osc.frequency.exponentialRampToValueAtTime(140 * pitchScale, t + 0.22);
    oscGain.gain.setValueAtTime(0.35, t);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);

    // Lowpass with slightly heavier resonance for that "orby" wub
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 900;
    lp.Q.value = 6;

    osc.connect(lp);
    lp.connect(oscGain);
    oscGain.connect(output);
    osc.start(t);
    osc.stop(t + 0.28);

    // Sub harmonic (one octave down) — adds weight
    const sub = ctx.createOscillator();
    const subGain = ctx.createGain();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(55 * pitchScale, t);
    sub.frequency.exponentialRampToValueAtTime(115 * pitchScale, t + 0.07);
    sub.frequency.exponentialRampToValueAtTime(70 * pitchScale, t + 0.22);
    subGain.gain.setValueAtTime(0.18, t);
    subGain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    sub.connect(subGain);
    subGain.connect(output);
    sub.start(t);
    sub.stop(t + 0.22);

    // Soft attack click — lower-passed than boing's, so it doesn't tick
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.02, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.3;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const nGain = ctx.createGain();
    nGain.gain.setValueAtTime(0.06, t);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 0.02);
    const lp2 = ctx.createBiquadFilter();
    lp2.type = 'lowpass';
    lp2.frequency.value = 1500;
    noise.connect(lp2);
    lp2.connect(nGain);
    nGain.connect(output);
    noise.start(t);
    noise.stop(t + 0.02);
  }

  /** Wheel tick — short noise pop, fires per wedge crossing during a spin. */
  wheelTick(): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.025), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.025);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800;
    bp.Q.value = 5;
    noise.connect(bp);
    bp.connect(gain);
    gain.connect(output);
    noise.start(t);
    noise.stop(t + 0.025);
  }

  /** Reward fanfare — quick arpeggiated chord on win. */
  rewardFanfare(): void {
    const ctx = this.engine.context;
    if (!ctx) return;
    const t = ctx.currentTime;
    const output = this.engine.createSfxGain();

    // C major arpeggio: C5, E5, G5, C6.
    const notes = [523.25, 659.25, 783.99, 1046.50];
    for (let i = 0; i < notes.length; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = notes[i];
      const start = t + i * 0.09;
      gain.gain.setValueAtTime(0.22, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
      osc.connect(gain);
      gain.connect(output);
      osc.start(start);
      osc.stop(start + 0.4);
    }
  }
}
